import { DocumentNode, gql, useMutation } from '@apollo/client';
import { Button, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { useCurrent } from '@/hooks/useCurrent';
import { useSignUpPrompt } from '@/hooks/useSignUpPrompt';
import { logError } from '@/imports/logging/ClientLogger';
import { organization_followed, organization_unfollowed } from '@/analytics';
import { RequestDemoButton } from '@/imports/ui/components/RequestDemoButton';

import {
  FeaturedSponsorButton_FollowSponsor,
  FeaturedSponsorButton_FollowSponsorVariables,
} from './graphql-types/FeaturedSponsorButton_FollowSponsor';
import { FeaturedSponsorButton_SponsorFragment } from './graphql-types/FeaturedSponsorButton_SponsorFragment';
import {
  FeaturedSponsorButton_UnfollowSponsor,
  FeaturedSponsorButton_UnfollowSponsorVariables,
} from './graphql-types/FeaturedSponsorButton_UnfollowSponsor';

const FOLLOW_SPONSOR = gql`
  mutation FeaturedSponsorButton_FollowSponsor(
    $input: CreateUserOrganizationLinkInput!
  ) {
    createUserOrganizationLink(input: $input) {
      data {
        _id
        organization {
          _id
          userOrganizationLink {
            _id
          }
        }
      }
    }
  }
`;

const UNFOLLOW_SPONSOR = gql`
  mutation FeaturedSponsorButton_UnfollowSponsor(
    $input: DeleteUserOrganizationLinkInput!
  ) {
    deleteUserOrganizationLink(input: $input) {
      data {
        _id
        organization {
          _id
          userOrganizationLink {
            _id
          }
        }
      }
    }
  }
`;

const SPONSOR_FRAGMENT = gql`
  fragment FeaturedSponsorButton_SponsorFragment on Organization {
    _id
    name
    userOrganizationLink {
      _id
    }
    sponsorship {
      requestDemoTitle
      currentTier {
        analyticsTierType
      }
    }
  }
`;

export const FeaturedSponsorButton: React.FC<{
  featuredSponsor: FeaturedSponsorButton_SponsorFragment;
}> & { sponsorFragment: DocumentNode } = (props) => {
  const { featuredSponsor } = props;
  const [createUserOrganizationLink, createUserOrganizationLinkResult] =
    useMutation<
      FeaturedSponsorButton_FollowSponsor,
      FeaturedSponsorButton_FollowSponsorVariables
    >(FOLLOW_SPONSOR);

  const [deleteUserOrganizationLink, deleteUserOrganizationLinkResult] =
    useMutation<
      FeaturedSponsorButton_UnfollowSponsor,
      FeaturedSponsorButton_UnfollowSponsorVariables
    >(UNFOLLOW_SPONSOR);

  const current = useCurrent();

  const [newFollow, setNewFollow] = React.useState(false);

  const toast = useToast();

  const {
    promptModal,
    loading: signUpPromptLoading,
    showPrompt,
  } = useSignUpPrompt();

  const onClickFollow = async (): Promise<void> => {
    if (!current.user?._id) {
      showPrompt();
      return;
    }

    try {
      await createUserOrganizationLink({
        variables: {
          input: {
            data: {
              userId: current.user._id,
              organizationId: featuredSponsor._id,
            },
          },
        },
      });
      organization_followed({
        entity_type: 'organization',
        entity_id: featuredSponsor._id,
        organization_name: featuredSponsor.name,
      });
      toast({
        description: `Successfully followed ${featuredSponsor.name}`,
        status: 'success',
      });
      setNewFollow(true);
    } catch (err) {
      toast({
        description: `Failed to follow ${featuredSponsor.name}`,
        status: 'error',
      });
      logError(err);
    }
  };

  const onClickUnfollow = async (): Promise<void> => {
    try {
      await deleteUserOrganizationLink({
        variables: {
          input: { id: featuredSponsor.userOrganizationLink?._id },
        },
      });
      organization_unfollowed({
        entity_type: 'organization',
        entity_id: featuredSponsor._id,
        organization_name: featuredSponsor.name,
      });
      toast({
        description: `Successfully unfollowed ${featuredSponsor.name}`,
        status: 'success',
      });
      setNewFollow(false);
    } catch (err) {
      toast({
        description: `Failed to unfollow ${featuredSponsor.name}`,
        status: 'error',
      });
      logError(err);
    }
  };

  const registerInterestTitle =
    featuredSponsor.sponsorship?.requestDemoTitle || 'Request a Demo';

  if (current.loading || signUpPromptLoading) {
    return null;
  }

  if (!!current.user?._id && newFollow) {
    return (
      <Button
        variant="outline"
        w="100%"
        colorScheme="gray"
        size="md"
        isLoading={deleteUserOrganizationLinkResult.loading}
        onClick={onClickUnfollow}
      >
        Following
      </Button>
    );
  }

  if (!!current.user?._id && !!featuredSponsor.userOrganizationLink?._id) {
    return (
      <RequestDemoButton
        options={{
          organizationId: featuredSponsor._id,
          origin: { featuredSponsorCard: true },
          registerInterestTitle: registerInterestTitle,
          analytics: {
            view: 'sponsorPage',
            tier:
              featuredSponsor.sponsorship?.currentTier.analyticsTierType || '',
          },
        }}
        variant="outline"
        w="100%"
        colorScheme="gray"
        size="md"
      >
        {registerInterestTitle}
      </RequestDemoButton>
    );
  }
  return (
    <>
      {promptModal}
      <Button
        variant="outline"
        w="100%"
        colorScheme="gray"
        size="md"
        isLoading={createUserOrganizationLinkResult.loading}
        onClick={onClickFollow}
      >
        Follow
      </Button>
    </>
  );
};

FeaturedSponsorButton.sponsorFragment = SPONSOR_FRAGMENT;
